import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Download, Upload, Search, ChevronDown, ChevronUp } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import * as XLSX from 'xlsx';
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { useQuery } from "@tanstack/react-query";

type MedicalAlert = {
  type: 'DRUG_ALLERGY' | 'SURGERY_HISTORY' | 'OTHER';
  description: string;
};

type Student = {
  id: string;
  surname: string;
  other_names: string | null;
  class: string;
  stream: string | null;
  medical_alerts: MedicalAlert[];
};

type StudentFromDB = Omit<Student, 'medical_alerts'> & {
  medical_alerts: any[];
};

export function MedicalAlertsTemplate() {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStudents, setSelectedStudents] = useState<Set<string>>(new Set());
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  // Use React Query for data fetching
  const { data: students = [], isLoading, refetch } = useQuery({
    queryKey: ['students', user?.schoolId],
    queryFn: async () => {
      if (!user?.schoolId) {
        console.error("No school ID found for user");
        return [];
      }
      
      const { data: studentsData, error: studentsError } = await supabase
        .from('students')
        .select('id, surname, other_names, class, stream, medical_alerts')
        .eq('school_id', user.schoolId)
        .eq('is_archived', false)
        .order('surname');

      if (studentsError) {
        console.error("Error fetching students:", studentsError);
        throw studentsError;
      }

      return (studentsData as StudentFromDB[]).map(student => ({
        ...student,
        medical_alerts: Array.isArray(student.medical_alerts) 
          ? student.medical_alerts.map(alert => ({
              type: alert.type as MedicalAlert['type'],
              description: alert.description as string
            }))
          : []
      }));
    },
    gcTime: 5 * 60 * 1000, // 5 minutes
  });

  // Filter students based on search query - now more efficient
  const filteredStudents = students.filter(student => 
    student.surname.toLowerCase().includes(searchQuery.toLowerCase()) ||
    student.other_names?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    student.class.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleStudent = (studentId: string) => {
    const newSelected = new Set(selectedStudents);
    if (newSelected.has(studentId)) {
      newSelected.delete(studentId);
    } else {
      newSelected.add(studentId);
    }
    setSelectedStudents(newSelected);
  };

  const generateTemplate = async () => {
    if (!user?.schoolId || selectedStudents.size === 0) {
      toast({
        title: "Error",
        description: "Please select at least one student",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsGenerating(true);
      
      const selectedStudentsData = students
        .filter(student => selectedStudents.has(student.id))
        .map(student => ({
          ID: student.id,
          Surname: student.surname,
          'Other Names': student.other_names,
          Class: student.class,
          Stream: student.stream,
          'Drug Allergies': student.medical_alerts
            .filter(a => a.type === 'DRUG_ALLERGY')
            .map(a => a.description)
            .join(', '),
          'Surgery History': student.medical_alerts
            .filter(a => a.type === 'SURGERY_HISTORY')
            .map(a => a.description)
            .join(', '),
          'Other Medical Conditions': student.medical_alerts
            .filter(a => a.type === 'OTHER')
            .map(a => a.description)
            .join(', ')
        }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(selectedStudentsData);

      // Set column widths for better readability
      ws['!cols'] = [
        { wch: 10 }, // ID
        { wch: 15 }, // Surname
        { wch: 15 }, // Other Names
        { wch: 10 }, // Class
        { wch: 10 }, // Stream
        { wch: 30 }, // Drug Allergies
        { wch: 30 }, // Surgery History
        { wch: 30 }, // Other Medical Conditions
      ];

      XLSX.utils.book_append_sheet(wb, ws, "Students");
      XLSX.writeFile(wb, "medical_alerts_template.xlsx");

      toast({
        title: "Success",
        description: "Template generated successfully",
      });
    } catch (error) {
      console.error("Error generating template:", error);
      toast({
        title: "Error",
        description: "Failed to generate template",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user?.schoolId) {
      toast({
        title: "Error",
        description: "Please select a file and ensure you're logged in",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);
    let successCount = 0;
    let errorCount = 0;
    let errorMessages: string[] = [];
    
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      for (const row of jsonData as any[]) {
        if (!row.ID) {
          errorCount++;
          errorMessages.push(`Row missing ID`);
          continue;
        }

        try {
          const medicalAlerts: MedicalAlert[] = [];

          // Process Drug Allergies
          if (row['Drug Allergies']) {
            const allergies = row['Drug Allergies'].toString().split(',');
            allergies.forEach(allergy => {
              const trimmedAllergy = allergy.trim();
              if (trimmedAllergy) {
                medicalAlerts.push({
                  type: 'DRUG_ALLERGY',
                  description: trimmedAllergy
                });
              }
            });
          }

          // Process Surgery History
          if (row['Surgery History']) {
            const surgeries = row['Surgery History'].toString().split(',');
            surgeries.forEach(surgery => {
              const trimmedSurgery = surgery.trim();
              if (trimmedSurgery) {
                medicalAlerts.push({
                  type: 'SURGERY_HISTORY',
                  description: trimmedSurgery
                });
              }
            });
          }

          // Process Other Medical Conditions
          if (row['Other Medical Conditions']) {
            const conditions = row['Other Medical Conditions'].toString().split(',');
            conditions.forEach(condition => {
              const trimmedCondition = condition.trim();
              if (trimmedCondition) {
                medicalAlerts.push({
                  type: 'OTHER',
                  description: trimmedCondition
                });
              }
            });
          }

          const { error: updateError } = await supabase
            .from('students')
            .update({ medical_alerts: medicalAlerts })
            .eq('id', row.ID)
            .eq('school_id', user.schoolId);

          if (updateError) {
            errorCount++;
            errorMessages.push(`Failed to update student ${row.ID}: ${updateError.message}`);
          } else {
            successCount++;
          }
        } catch (error) {
          errorCount++;
          errorMessages.push(`Error processing student ${row.ID}: ${error}`);
        }
      }

      // Refresh the data
      await refetch();

      toast({
        title: successCount > 0 ? "Success" : "Error",
        description: `Updated ${successCount} student${successCount !== 1 ? 's' : ''}${
          errorCount > 0 ? `. Failed to update ${errorCount} student${errorCount !== 1 ? 's' : ''}.` : '.'
        }${errorMessages.length > 0 ? '\nErrors: ' + errorMessages.slice(0, 3).join('\n') : ''}`,
        variant: successCount > 0 ? "default" : "destructive",
      });
    } catch (error) {
      console.error("Error uploading medical alerts:", error);
      toast({
        title: "Error",
        description: "Failed to process the file. Please check the format and try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      if (e.target) {
        e.target.value = '';
      }
    }
  };

  return (
    <Card>
      <CardHeader className="cursor-pointer" onClick={() => setIsCollapsed(!isCollapsed)}>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Medical Alerts Template</CardTitle>
            <CardDescription>
              Select students to generate a template for updating their medical alerts
            </CardDescription>
          </div>
          {isCollapsed ? <ChevronDown className="h-6 w-6" /> : <ChevronUp className="h-6 w-6" />}
        </div>
      </CardHeader>
      {!isCollapsed && (
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search students..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                Selected: {selectedStudents.size}
              </span>
            </div>
          </div>

          <div className="border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12"></TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Class</TableHead>
                  <TableHead>Stream</TableHead>
                  <TableHead>Medical Alerts</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-4">
                      Loading students...
                    </TableCell>
                  </TableRow>
                ) : filteredStudents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-4">
                      No students found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredStudents.map((student) => (
                    <TableRow key={student.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedStudents.has(student.id)}
                          onCheckedChange={() => toggleStudent(student.id)}
                        />
                      </TableCell>
                      <TableCell>
                        {student.surname} {student.other_names}
                      </TableCell>
                      <TableCell>{student.class}</TableCell>
                      <TableCell>{student.stream}</TableCell>
                      <TableCell>
                        {student.medical_alerts && student.medical_alerts.length > 0 ? (
                          <ul className="list-disc list-inside">
                            {student.medical_alerts.map((alert, index) => (
                              <li key={index}>
                                {alert.type.replace(/_/g, ' ')}: {alert.description}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <span className="text-muted-foreground">No medical alerts</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex flex-col gap-4 sm:flex-row">
            <Button
              onClick={generateTemplate}
              disabled={isGenerating || selectedStudents.size === 0}
              className="w-full sm:w-auto"
            >
              <Download className="mr-2 h-4 w-4" />
              {isGenerating ? "Generating..." : "Download Template"}
            </Button>
            
            <div className="relative w-full sm:w-auto">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                disabled={isUploading}
                className="absolute inset-0 cursor-pointer opacity-0"
              />
              <Button
                variant="outline"
                className="w-full sm:w-auto"
                disabled={isUploading}
              >
                <Upload className="mr-2 h-4 w-4" />
                {isUploading ? "Uploading..." : "Upload Template"}
              </Button>
            </div>
          </div>
          
          <div className="text-sm text-muted-foreground">
            Download the template, fill in the medical alerts information (separate multiple entries with commas), and upload it back to update student records.
          </div>
        </CardContent>
      )}
    </Card>
  );
}
